import { useCallback, useLayoutEffect, useRef } from "react";

/** If the user is within this many px of the bottom, new messages keep the view pinned. */
const NEAR_BOTTOM_PX = 80;

function scrollListToBottom(el: HTMLDivElement) {
  el.scrollTop = el.scrollHeight;
}

/**
 * Keeps the message list scrolled to the latest when the user is already at the bottom,
 * or after {@link pinToBottom} (e.g. right after sending). Does not jump if the user scrolled up to read history.
 * When the thread changes or the list becomes active again, always jumps to the end.
 */
export function useChatMessagesAutoScroll(
  threadId: number,
  messages: readonly { id: number }[],
  active: boolean,
) {
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const prevThreadIdRef = useRef<number | null>(null);
  const prevActiveRef = useRef(active);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = dist < NEAR_BOTTOM_PX;
  }, []);

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]!.id : 0;
  const messageCount = messages.length;

  useLayoutEffect(() => {
    const el = listRef.current;
    const threadChanged = prevThreadIdRef.current !== threadId;
    const becameActive = !prevActiveRef.current && active;

    prevThreadIdRef.current = threadId;
    prevActiveRef.current = active;

    if (!active) return;
    if (!el) return;

    if (threadChanged || becameActive) {
      stickToBottomRef.current = true;
      scrollListToBottom(el);
      requestAnimationFrame(() => {
        const again = listRef.current;
        if (again) scrollListToBottom(again);
      });
      return;
    }

    if (!stickToBottomRef.current) return;
    scrollListToBottom(el);
  }, [active, threadId, lastMessageId, messageCount]);

  const pinToBottom = useCallback(() => {
    stickToBottomRef.current = true;
  }, []);

  return { listRef, onScroll, pinToBottom };
}
